import { CHAPTERS, TOTAL_STAGES, ALL_STAGES } from "./missions.js";
import { earnedUpgrades, isUnlocked, stageKey, availableCheckpoint } from "./rules.js";
import { EXPEDITIONS } from "./expedition-data.js";
import { EQUIPMENT, PAINTS } from "./equipment.js";
import { Minimap } from "./minimap.js";
import { fieldChallenge } from "./mission-report.js";
import { DISTRICTS, storyFor, journalEntries } from "./story.js";

// Touch players see the on-screen button names instead of keyboard keys.
export function touchPrompt(text) {
  return text.replace(/^E \/ Q · /, "USE / PULSE · ").replace(/^E · /, "USE · ").replace(/^Q · /, "PULSE · ");
}

export class UI {
  constructor(progress, callbacks) {
    this.progress = progress;
    this.callbacks = callbacks;
    this.screens = ["loading", "titleScreen", "mapScreen", "briefing", "expeditionScreen", "workshopScreen"];
    this.minimap = new Minimap(document.querySelector("#minimap"));
    this.hud = document.querySelector("#hud");
    this.toast = document.querySelector("#message");
    this.touch = document.querySelector("#touchControls");
    this.cache = {};
    this.messageTimer = 0;
    this.pulseMeter = document.querySelector("#pulseMeter");
    this.dashMeter = document.querySelector("#dashMeter");
    this.touchMeters = { pulse: document.querySelector('[data-touch="pulse"]'), dash: document.querySelector('[data-touch="dash"]') };
    this.bind();
  }

  bind() {
    document.querySelector("#journalButton").addEventListener("click", () => this.showJournal());
    document.querySelector("#resumeCheckpoint").addEventListener("click", () => this.callbacks.resumeCheckpoint());
    document.querySelector("#campaignModeSelect").addEventListener("change", event => {
      this.callbacks.settings({ campaignMode: event.target.value });
      this.showBriefing(this.selected);
    });
    document.querySelector("#nextDialogue").addEventListener("click", event => { if (event.detail) event.currentTarget.blur(); this.advanceDialogue(); });
    document.querySelector("#expeditionsButton").addEventListener("click", () => this.showExpeditions());
    document.querySelector("#workshopButton").addEventListener("click", () => this.showWorkshop());
    document.querySelector("#expeditionWorkshop").addEventListener("click", () => this.showWorkshop());
    document.querySelector("#workshopPlay").addEventListener("click", () => this.showExpeditions());
    document.querySelector("#briefBack").addEventListener("click", () => this.selected?.stage.type === "expedition" ? this.showExpeditions() : this.showMap());
    document.querySelector("#continueButton").addEventListener("click", () => this.callbacks.continue());
    document.querySelector("#brawlButton").addEventListener("click", () => this.callbacks.brawl());
    document.querySelector("#missionsButton").addEventListener("click", () => this.showMap());
    document.querySelector("#roamButton").addEventListener("click", () => this.callbacks.roam());
    document.querySelectorAll("[data-screen='title']").forEach(button => button.addEventListener("click", () => this.showTitle()));
    document.querySelectorAll("[data-screen='map']").forEach(button => button.addEventListener("click", () => this.showMap()));
    document.querySelector("#launchButton").addEventListener("click", () => this.callbacks.launch(this.selected));
    // Mouse clicks must not leave focus on the pause button, or Space/Enter would pause instead of dash/use.
    document.querySelector("#pauseButton").addEventListener("click", event => { event.currentTarget.blur(); this.callbacks.pause(); });
    // A held key must never auto-repeat into the first button of a freshly opened result or pause menu.
    document.addEventListener("keydown", event => {
      if (event.repeat && event.target.closest?.("#modal, #celebration")) event.preventDefault();
    }, true);
    document.querySelector("#howButton").addEventListener("click", () => this.showHelp());
    document.querySelector("#closeHelp").addEventListener("click", () => this.hideHelp());
    // Escape closes the manual, or resumes from a pause menu. Stopping here keeps the same key press
    // from reaching gameplay input, which would pause again on the next frame.
    document.addEventListener("keydown", event => {
      if (event.key !== "Escape" || event.repeat) return;
      if (!document.querySelector("#helpPanel").classList.contains("hidden")) { event.stopPropagation(); this.hideHelp(); return; }
      const modal = document.querySelector("#modal");
      if (!modal.classList.contains("hidden") && this.dismiss) { event.stopPropagation(); const run = this.dismiss; modal.classList.add("hidden"); this.dismiss = null; run(); }
    });
    // While any dialog is open, everything behind it is inert: Tab and clicks cannot reach the HUD or menus.
    const dialogs = ["#modal", "#celebration", "#helpPanel"].map(selector => document.querySelector(selector));
    const syncInert = () => {
      const open = dialogs.find(dialog => !dialog.classList.contains("hidden"));
      for (const element of document.querySelector("#app").children) {
        if (element === this.toast) continue;
        element.inert = Boolean(open) && element !== open;
      }
    };
    this.syncInert = syncInert; // also called directly before focusing a dialog, since observers run late
    const observer = new MutationObserver(syncInert);
    for (const dialog of dialogs) observer.observe(dialog, { attributes: true, attributeFilter: ["class"] });
    this.applyMotion(this.progress.settings.reducedMotion);
    document.querySelector("#resetButton").addEventListener("click", () => this.callbacks.reset());
    document.querySelector("#quality").value = this.progress.settings.quality || "auto";
    document.querySelector("#quality").addEventListener("change", event => this.callbacks.settings({ quality: event.target.value }));
    document.querySelector("#reducedMotion").checked = this.progress.settings.reducedMotion;
    document.querySelector("#reducedMotion").addEventListener("change", event => { this.applyMotion(event.target.checked); this.callbacks.settings({ reducedMotion: event.target.checked }); });
    document.querySelector("#soundEnabled").checked = this.progress.settings.sound !== false;
    document.querySelector("#soundEnabled").addEventListener("change", event => this.callbacks.settings({ sound: event.target.checked }));
    document.querySelector("#skipDialogue").addEventListener("click", event => { if (event.detail) event.currentTarget.blur(); this.hideDialogue(); });
  }

  setProgress(progress) {
    this.progress = progress;
    this.updateCampaign();
  }

  loading(amount, label) {
    document.querySelector("#loadBar").style.width = `${Math.round(amount * 100)}%`;
    document.querySelector("#loadStatus").textContent = amount >= 1 ? "Island ready" : `Loading ${label.replaceAll("_", " ")}…`;
  }

  showHelp() {
    this.helpReturn = document.activeElement;
    document.querySelector("#helpPanel").classList.remove("hidden");
    this.syncInert();
    document.querySelector("#closeHelp").focus();
  }
  hideHelp() {
    const panel = document.querySelector("#helpPanel");
    if (panel.classList.contains("hidden")) return;
    panel.classList.add("hidden");
    this.syncInert();
    const back = this.helpReturn; this.helpReturn = null;
    if (back?.isConnected && back.getClientRects().length) back.focus();
  }
  // CSS follows the in-game setting; html.motion-ok overrides an OS preference the player turned off.
  applyMotion(reduced) {
    const root = document.documentElement;
    root.classList.toggle("reduced-motion", Boolean(reduced));
    root.classList.toggle("motion-ok", !reduced);
  }
  showOnly(id) {
    this.hideCelebration();
    this.hideDialogue();
    this.hideHelp();
    this.toast.classList.remove("combat-active");
    if (id !== "workshopScreen") this.callbacks.preview?.(false);
    this.screens.forEach(screen => document.querySelector(`#${screen}`).classList.toggle("hidden", screen !== id));
    this.hud.classList.add("hidden");
    this.touch.classList.add("hidden");
    document.querySelector("#modal").classList.add("hidden");
  }

  showTitle() {
    this.showOnly("titleScreen");
    this.callbacks.titleScene?.();
    this.updateCampaign();
  }

  showExpeditions() {
    this.showOnly("expeditionScreen");
    document.querySelector("#expeditionCards").replaceChildren(...EXPEDITIONS.map(item => {
      const stage = item.stage, best = this.progress.expeditionResults[stage.id];
      const card = document.createElement("article"); card.className = "expedition-card glass";
      card.style.setProperty("--stage-color", stage.color);
      card.innerHTML = `<div class="expedition-art" aria-hidden="true">${stage.icon}</div><p class="eyebrow">${stage.difficulty}</p><h3>${stage.name}</h3><p>${stage.story}</p><small>Discover · ${stage.reward}</small><span class="expedition-score">${best != null ? `✓ Completed · best ${best}` : "New shore · ready to explore"}</span>`;
      const play = document.createElement("button"); play.className = "primary"; play.textContent = `Play ${stage.name}`;
      play.addEventListener("click", () => this.showBriefing(item)); card.append(play);
      return card;
    }));
  }

  showWorkshop() {
    this.showOnly("workshopScreen");
    this.renderWorkshop();
  }

  renderWorkshop() {
    const equipment = this.progress.equipment;
    document.querySelector("#workshopSummary").textContent = `${equipment.owned.length} / 6 discoveries · ${PAINTS[equipment.paint].name}`;
    document.querySelector("#paintChoices").replaceChildren(...Object.entries(PAINTS).map(([id, paint]) => {
      const button = document.createElement("button"); button.className = "paint-choice";
      button.style.setProperty("--paint", `#${paint.shell.toString(16).padStart(6, "0")}`);
      button.textContent = paint.name; button.setAttribute("aria-pressed", String(equipment.paint === id));
      button.addEventListener("click", () => { this.callbacks.paint(id); this.renderWorkshop(); });
      return button;
    }));
    document.querySelector("#equipmentChoices").replaceChildren(...Object.entries(EQUIPMENT).map(([id, data]) => {
      const owned = equipment.owned.includes(id), selected = equipment.equipped[data.slot] === id;
      const button = document.createElement("button"); button.className = "equipment-choice";
      button.disabled = !owned; button.setAttribute("aria-pressed", String(selected));
      button.innerHTML = `<strong>${selected ? "✓ " : ""}${data.name}</strong><small>${data.slot.toUpperCase()} · ${data.description}</small><span>${!owned ? `Find in ${data.found}` : selected ? "Equipped · click to remove" : "Click to equip"}</span>`;
      button.addEventListener("click", () => { this.callbacks.equip(id, selected); this.renderWorkshop(); });
      return button;
    }));
    this.callbacks.preview?.(true);
  }

  updateCampaign() {
    const complete = ALL_STAGES.filter(item => this.progress.completed.includes(stageKey(item.chapter.id, item.stage.id))).length;
    document.querySelector("#campaignCount").textContent = `${complete} / ${TOTAL_STAGES}`;
    document.querySelector("#campaignBar").style.width = `${(complete / TOTAL_STAGES) * 100}%`;
    document.querySelector("#continueButton").textContent = complete ? "Continue journey" : "Begin journey";
    document.querySelector("#roamButton").classList.toggle("hidden", !earnedUpgrades(this.progress).freeRoam);
    document.querySelector("#resumeCheckpoint").classList.toggle("hidden", ALL_STAGES.at(-1).stage.type !== "finale" || !availableCheckpoint(this.progress));
    document.querySelector("#migrationNote").classList.toggle("hidden", !this.progress.previousCampaign?.completed.length);
  }

  showJournal() {
    const entries = journalEntries(this.progress, ALL_STAGES);
    const archive = this.progress.previousCampaign?.completed.length;
    this.modal({ icon: "◈", eyebrow: "Discovery atlas", title: "Every place leaves a story", text: (entries.length
      ? entries.map(entry => `${entry.district} — ${entry.title}\n${entry.discovery}\n${entry.text}`).join("\n\n")
      : "The first page is waiting in Meridian City. Only completed destinations appear here; future discoveries stay secret.") + (archive ? `\n\nEarlier campaign archive: ${archive} completed missions. Your previous medals are preserved in your save.` : ""),
      actions: [{ label: "Back to title", run: () => this.showTitle() }] });
  }

  showMap() {
    this.showOnly("mapScreen");
    const rail = document.querySelector("#chapterRail");
    rail.replaceChildren(...CHAPTERS.map((chapter, chapterIndex) => {
      const card = document.createElement("article");
      card.className = "chapter-card glass";
      card.style.setProperty("--chapter-color", chapter.color);
      const complete = chapter.stages.filter(stage => this.progress.completed.includes(stageKey(chapter.id, stage.id))).length;
      card.innerHTML = `<div class="chapter-number">${chapter.number}</div><div class="chapter-copy"><span>${chapter.icon}</span><p class="eyebrow">${DISTRICTS[chapter.id].name} · ${complete}/3</p><h3>${chapter.name}</h3><p>${complete === 3 ? DISTRICTS[chapter.id].restored : chapter.summary}</p><small>Reward · ${chapter.reward}</small></div><div class="stage-dots"></div>`;
      const dots = card.querySelector(".stage-dots");
      chapter.stages.forEach((stage, stageIndex) => {
        const button = document.createElement("button");
        const unlocked = isUnlocked(this.progress, chapterIndex, stageIndex);
        const result = this.progress.results[stageKey(chapter.id, stage.id)];
        button.disabled = !unlocked;
        button.className = result ? "complete" : "";
        button.dataset.scene = stage.scene;
        button.innerHTML = `<span>${unlocked ? chapterIndex * 3 + stageIndex + 1 : "🔒"}</span><strong>${stage.name}</strong><em class="stop-location">${stage.location}</em><small>${stage.activity}</small><small>${result ? `${"◆".repeat(result.medals)}${"◇".repeat(3 - result.medals)}` : unlocked ? "Ready to discover" : "Complete the previous destination"}</small>`;
        button.addEventListener("click", () => this.showBriefing({ chapter, stage, chapterIndex, stageIndex }));
        dots.append(button);
      });
      return card;
    }));
  }

  showBriefing(item) {
    this.selected = item;
    this.showOnly("briefing");
    document.querySelector("#briefIcon").textContent = item.chapter.icon;
    document.querySelector("#briefChapter").textContent = item.stage.type === "expedition" ? `Expedition ${item.stageIndex + 1} · ${item.stage.difficulty}` : item.stage.type === "brawl" ? "Arcade · 3 waves · available now" : `Chapter ${item.chapter.number} · Mission ${item.stageIndex + 1}`;
    document.querySelector("#briefTitle").textContent = item.stage.name;
    const narrative = storyFor(item);
    document.querySelector("#briefLocation").textContent = narrative ? item.stage.location || DISTRICTS[item.chapter.id].name : "Beyond the story route";
    document.querySelector("#briefStakes").textContent = narrative?.stakes || "Explore, experiment, and bring your discoveries home.";
    document.querySelector("#briefBeats").replaceChildren(...(narrative?.beats || []).map(text => { const li = document.createElement("li"); li.textContent = text; return li; }));
    document.querySelector("#campaignModeControl").classList.toggle("hidden", !narrative);
    document.querySelector("#campaignModeSelect").value = this.progress.settings.campaignMode;
    document.querySelector("#briefPace").textContent = narrative ? this.progress.settings.campaignMode === "story"
      ? `No campaign deadline. One activity completes this stop. Shield damage still matters. Par time: ${Math.floor(item.stage.time * .75)} active seconds for the speed medal.`
      : `Timed mission: ${item.stage.time} seconds. Optional medals never block the next destination.`
      : `Arcade / expedition limit: ${item.stage.time} seconds. Campaign mode does not change this activity.`;
    document.querySelector("#briefStory").textContent = item.stage.story;
    document.querySelector("#briefObjective").textContent = item.stage.objective;
    document.querySelector("#briefChallenge").textContent = `Optional challenge · ${fieldChallenge(item.stage)?.text || "Explore at your own pace"}. ${["brawl", "expedition"].includes(item.stage.type) ? "+500 score on victory." : "Earn the third medal."}`;
    document.querySelector("#briefReward").textContent = item.stage.scene ? `Next destination · ${ALL_STAGES[item.chapterIndex * 3 + item.stageIndex + 1]?.stage.location || "Home, with a complete discovery atlas"}` : item.stage.type === "expedition" ? `Discover · ${item.stage.reward}` : "Weapons · Freeze Pops · Crab crew · Repaired teammates";
  }

  showGame(item) {
    this.hideCelebration();
    this.hideHelp();
    document.querySelector("#modal").classList.add("hidden");
    this.screens.forEach(screen => document.querySelector(`#${screen}`).classList.add("hidden"));
    this.hud.classList.remove("hidden");
    this.touch.classList.toggle("hidden", !matchMedia("(pointer: coarse)").matches);
    this.write("hudChapter", item.stage.type === "expedition" ? `Expedition ${item.stageIndex + 1} · ${item.stage.theme}` : item.stage.type === "brawl" ? "Beach Brawl · survive the party" : item.stage.location || "Restored island");
    this.write("hudTitle", item.stage.name);
    this.write("hudObjective", item.stage.activity || item.stage.objective);
    this.write("fieldChallenge", "");
  }

  updateHUD(state) {
    this.write("hudTimeLabel", state.storyMode ? "PLAY TIME" : "TIME LEFT");
    this.write("hudTime", state.untimed ? "—" : state.storyMode ? `${Math.floor(state.elapsed / 60)}:${String(Math.floor(state.elapsed % 60)).padStart(2, "0")}` : Math.max(0, Math.ceil(state.time)));
    this.write("hudProgress", state.progressText);
    this.write("hudProgressIcon", state.progressIcon || "⚡");
    document.querySelector("#fieldChallenge").classList.toggle("hidden", !state.challenge);
    if (state.challenge) this.write("fieldChallenge", `${state.challenge.goal && state.challenge.done ? "✓" : "◇"} Optional · ${state.challenge.label}`);
    this.write("hudShield", `${"◆".repeat(state.shields)}${"◇".repeat(Math.max(0, state.maxShields - state.shields))}`);
    const combat = state.combat;
    const active = Boolean(combat?.enabled);
    document.querySelector("#combatPanel").classList.toggle("hidden", !active);
    this.hud.classList.toggle("combat-active", active);
    this.touch.classList.toggle("combat-active", active);
    this.toast.classList.toggle("combat-active", active);
    if (active) {
      this.write("weaponName", combat.weapon === "arc" ? "Arc Fork" : combat.weapon ? "Bubble Blaster" : "Find a weapon crate");
      this.write("weaponAmmo", combat.weapon ? `${combat.ammo} shots · ${matchMedia("(pointer: coarse)").matches ? "hold Fire" : "hold J / mouse"}` : "Walk over a glowing crate");
      this.write("gadgetCount", `F Freeze ${combat.freezeCharges} · R Crabs ${combat.whistles}`);
      this.write("teamCount", `${combat.scrap} scrap · ${combat.game.entities.filter(e => e.kind === "ally").length}/3 robot pals`);
      this.write("touchFreeze", `Freeze ${combat.freezeCharges}`);
      this.write("touchCall", `Call ${combat.whistles}`);
    }
    for (const [id, element, value] of [["pulse", this.pulseMeter, state.pulseReady], ["dash", this.dashMeter, state.dashReady]]) {
      const rounded = Math.round(value * 100);
      if (this.cache[id] !== rounded) { element.style.setProperty("--ready", `${rounded}%`); this.touchMeters[id].style.setProperty("--ready", `${rounded}%`); this.cache[id] = rounded; }
    }
  }

  navigation(target, player, yaw) {
    const nav = document.querySelector("#navigation");
    nav.classList.toggle("hidden", !target);
    if (!target) return;
    const dx = target.x - player.x;
    const dz = target.z - player.z;
    const right = dx * Math.cos(yaw) - dz * Math.sin(yaw);
    const forward = -dx * Math.sin(yaw) - dz * Math.cos(yaw);
    const angle = Math.round(Math.atan2(right, forward) * 180 / Math.PI);
    if (this.cache.navAngle !== angle) { document.querySelector("#navArrow").style.transform = `rotate(${angle}deg)`; this.cache.navAngle = angle; }
    this.write("navLabel", target.label);
    this.write("navDistance", `${Math.round(Math.hypot(dx, dz))} m`);
  }

  write(id, value) {
    if (this.cache[id] === value) return;
    document.querySelector(`#${id}`).textContent = value;
    this.cache[id] = value;
  }

  prompt(text) {
    const element = document.querySelector("#prompt");
    if (this.cache.prompt !== text) {
      const touch = matchMedia("(pointer: coarse)").matches;
      element.textContent = touch ? touchPrompt(text || "") : text || "";
      element.classList.toggle("hidden", !text);
      this.cache.prompt = text;
    }
  }

  message(text, bad = false) {
    if (this.lastMessage === text && performance.now() - this.lastMessageTime < 1600) return;
    this.lastMessage = text; this.lastMessageTime = performance.now();
    this.callbacks.sound?.(bad ? "warning" : "note");
    const element = this.toast;
    clearTimeout(this.messageTimer);
    element.textContent = text;
    element.classList.toggle("bad", bad);
    element.classList.remove("hidden");
    this.messageTimer = setTimeout(() => element.classList.add("hidden"), 1900);
  }

  dialogueSnapshot() {
    return this.dialogueLines?.length ? { lines: this.dialogueLines, index: this.dialogueIndex, done: this.dialogueDone } : null;
  }

  suspendDialogue() {
    const snapshot = this.dialogueSnapshot();
    clearTimeout(this.dialogueTimer);
    document.querySelector("#dialogue").classList.add("hidden");
    this.dialogueLines = null; this.dialogueDone = null;
    return snapshot;
  }

  restoreDialogue(snapshot) {
    if (!snapshot?.lines?.[snapshot.index]) return;
    this.dialogueLines = snapshot.lines; this.dialogueIndex = snapshot.index; this.dialogueDone = snapshot.done;
    document.querySelector("#dialogue").classList.remove("hidden");
    this.renderDialogue();
  }

  flashDamage() {
    const flash = document.querySelector("#damageFlash");
    if (!flash) return;
    flash.classList.remove("active");
    void flash.offsetWidth;
    flash.classList.add("active");
  }

  dialogue(lines, onDone) {
    clearTimeout(this.dialogueTimer);
    if (!lines?.length) return onDone?.();
    this.dialogueLines = lines.map(entry => typeof entry === "string" ? { speaker: "LUMA", text: entry } : entry);
    this.dialogueIndex = 0; this.dialogueDone = onDone;
    document.querySelector("#dialogue").classList.remove("hidden");
    this.renderDialogue();
  }

  renderDialogue() {
    const line = this.dialogueLines?.[this.dialogueIndex];
    if (!line) return this.hideDialogue();
    document.querySelector("#speakerName").textContent = line.speaker;
    document.querySelector("#speakerPortrait").textContent = { LUMA: "🔆", BOLT: "🐕", KAI: "🤖", AURORA: "✦", WARDEN: "◈" }[line.speaker] || "◈";
    document.querySelector("#dialogueText").textContent = line.text;
    document.querySelector("#nextDialogue").textContent = this.dialogueIndex + 1 === this.dialogueLines.length ? "Done" : `Next · ${this.dialogueIndex + 1}/${this.dialogueLines.length}`;
  }

  advanceDialogue() {
    this.dialogueIndex++; this.renderDialogue();
  }

  hideDialogue() {
    clearTimeout(this.dialogueTimer);
    document.querySelector("#dialogue").classList.add("hidden");
    this.dialogueLines = null;
    const done = this.dialogueDone; this.dialogueDone = null; done?.();
  }

  showCelebration(stageName, onSkip, saved = true, story = null, journey = null) {
    this.hideDialogue();
    clearTimeout(this.messageTimer);
    this.hud.classList.add("hidden");
    this.touch.classList.add("hidden");
    document.querySelector("#modal").classList.add("hidden");
    document.querySelector("#celebrationStage").textContent = stageName;
    document.querySelector("#departureStory").textContent = story || "AURORA carries your discoveries to the next destination.";
    document.querySelector("#nextDestination").textContent = journey ? `Discovered: ${journey.discovery}\nNext: ${journey.next}` : "";
    document.querySelector("#celebrationSave").textContent = saved ? "Stage clear · progress saved" : "Stage clear · progress kept this session";
    const button = document.querySelector("#skipCelebration");
    button.onclick = onSkip;
    document.querySelector("#celebration").classList.remove("hidden");
    this.syncInert?.();
    button.focus();
  }

  hideCelebration() {
    document.querySelector("#celebration").classList.add("hidden");
    document.querySelector("#skipCelebration").onclick = null;
  }

  modal({ icon, eyebrow, title, text, stats = [], actions = [], dismiss = -1 }) {
    this.hideCelebration();
    this.hideDialogue();
    this.dismiss = actions[dismiss]?.run || null;
    const modal = document.querySelector("#modal");
    document.querySelector("#modalIcon").textContent = icon;
    document.querySelector("#modalEyebrow").textContent = eyebrow;
    document.querySelector("#modalTitle").textContent = title;
    document.querySelector("#modalText").textContent = text;
    document.querySelector("#modalStats").innerHTML = stats.map(([value, label]) => `<div><strong>${value}</strong><span>${label}</span></div>`).join("");
    const container = document.querySelector("#modalActions");
    container.replaceChildren(...actions.map((action, index) => {
      const button = document.createElement("button");
      button.textContent = action.label;
      if (index === 0) button.className = "primary";
      button.addEventListener("click", () => { modal.classList.add("hidden"); this.dismiss = null; action.run(); });
      return button;
    }));
    modal.classList.remove("hidden");
    this.syncInert?.();
    container.querySelector("button")?.focus();
  }
}
